import { useQuery } from '@tanstack/react-query'
import { Link } from 'react-router-dom'
import { superadminApi } from '@/api/superadmin'

const OUTCOME: Record<string, { label: string; classes: string }> = {
  Cancelled: { label: 'Cancelled', classes: 'bg-red-50 text-red-700 border-red-200' },
  TrialExtended: { label: 'Trial extended', classes: 'bg-indigo-50 text-indigo-700 border-indigo-200' },
  DiscountApplied: { label: 'Took discount', classes: 'bg-green-50 text-green-700 border-green-200' },
}

function fmt(iso: string) {
  return new Date(iso).toLocaleDateString('en-GB', { day: 'numeric', month: 'short', year: 'numeric' })
}

export default function CancellationsPage() {
  const { data: rows = [], isLoading } = useQuery({
    queryKey: ['superadmin-cancellations'],
    queryFn: () => superadminApi.getCancellations().then(r => r.data),
  })

  // Why people leave, counting only the ones who actually cancelled.
  const reasonCounts = Object.entries(
    rows.filter(r => r.outcome === 'Cancelled').reduce<Record<string, number>>((acc, r) => {
      const key = r.reasonLabel ?? 'No reason given'
      acc[key] = (acc[key] ?? 0) + 1
      return acc
    }, {}),
  ).sort((a, b) => b[1] - a[1])

  const saved = rows.filter(r => r.outcome !== 'Cancelled').length

  return (
    <div>
      <div className="mb-6">
        <h1 className="text-2xl font-bold text-gray-900">Cancellations</h1>
        <p className="text-sm text-gray-500 mt-1">
          What customers told us when they cancelled, or took an offer instead. {saved > 0 && `${saved} stayed after an offer.`}
        </p>
      </div>

      {reasonCounts.length > 0 && (
        <div className="bg-white border border-gray-200 rounded-xl p-5 mb-6 max-w-2xl">
          <p className="text-sm font-semibold text-gray-900 mb-3">Reasons for cancelling</p>
          <ul className="space-y-1.5 text-sm text-gray-600">
            {reasonCounts.map(([label, n]) => (
              <li key={label} className="flex justify-between gap-4"><span>{label}</span><span className="font-medium text-gray-900">{n}</span></li>
            ))}
          </ul>
        </div>
      )}

      <div className="bg-white border border-gray-200 rounded-xl shadow-sm overflow-x-auto">
        <table className="w-max min-w-full text-sm">
          <thead className="bg-gray-50 border-b border-gray-200">
            <tr>
              {['Date', 'Tenant', 'What happened', 'Plan', 'Reason', 'In their words', 'By'].map(h => (
                <th key={h} className="px-5 py-3 text-left text-xs font-semibold text-gray-500 uppercase tracking-wider">{h}</th>
              ))}
            </tr>
          </thead>
          <tbody className="divide-y divide-gray-100">
            {isLoading && <tr><td colSpan={7} className="text-center text-gray-400 py-12">Loading…</td></tr>}
            {!isLoading && rows.length === 0 && <tr><td colSpan={7} className="text-center text-gray-400 py-12">Nothing yet.</td></tr>}
            {rows.map(r => {
              const o = OUTCOME[r.outcome] ?? { label: r.outcome, classes: 'bg-gray-100 text-gray-600 border-gray-200' }
              return (
                <tr key={r.id} className="align-top hover:bg-gray-50/60">
                  <td className="px-5 py-3 text-gray-500 whitespace-nowrap">{fmt(r.createdAt)}</td>
                  <td className="px-5 py-3 font-medium">
                    <Link to={`/superadmin/tenants/${r.tenantId}`} className="text-indigo-600 hover:underline">{r.tenantName}</Link>
                  </td>
                  <td className="px-5 py-3"><span className={`inline-flex px-2 py-0.5 rounded-full text-xs font-medium border ${o.classes}`}>{o.label}</span></td>
                  <td className="px-5 py-3 text-gray-600 whitespace-nowrap">{r.planTier ?? '—'}{r.wasTrialing ? ' (trial)' : ''}</td>
                  <td className="px-5 py-3 text-gray-700">{r.reasonLabel ?? <span className="text-gray-300">—</span>}</td>
                  <td className="px-5 py-3 text-gray-600 max-w-sm">{r.reasonDetail ?? <span className="text-gray-300">—</span>}</td>
                  <td className="px-5 py-3 text-gray-500">{r.userEmail ?? '—'}</td>
                </tr>
              )
            })}
          </tbody>
        </table>
      </div>
    </div>
  )
}
